import React, { useContext, useEffect, useState } from "react";
import { SCREEN_PATHS } from "../../../constants";
import { Box, Button, Typography } from "@mui/material";
import { ProbateField } from "../../../models/ProbateField";
import { useNavigate } from "react-router";
import { ScreenContentWrapper } from "../../shared/ScreenContentWrapper";
import { ProbateFieldQuestion } from "./ProbateFieldQuestion";
import { probateApi } from "../../../apis";
import { Loading } from "../../shared/Loading";
import { LeadProbateContext } from "LeadProbateContextWrapper";
import { LeadContext } from "LeadContextWrapper";
import { CountryStateSelection } from "views/geography/CountryStateSelection";

export const LeadProbateQuestionScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { saveFieldAnswered, getFieldsAnswered } = useContext(LeadProbateContext)
  const { lead, leadData } = useContext(LeadContext)
  const [error, setError] = useState<string>();
  const [screenNumber, setScreenNumber] = useState(1);
  const [noProbateForState, setNoProbateForState] = useState(false);
  const [skipFields, setSkipFields] = useState<string[]>([]);
  const [currentField, setCurrentField] = useState<ProbateField>();

  useEffect(() => {
    getProbateDetailsAndField();
  }, [screenNumber, getFieldsAnswered, leadData, leadData ? leadData["country_id"] : null, leadData ? leadData["state_id"] : null]);

  const getProbateDetailsAndField = async () => {
    if(!getFieldsAnswered) {
      console.warn("getFieldsAnswered called before instantiation")
      return
    }
    if(!leadData ) {
      console.warn("we need lead data before we can get probate details/fields")
      return
    }
    if(!leadData["country_id"] || !leadData["state_id"]) {
      console.warn("we need country/state data before we can get probate details/fields")
      return
    }

    try {
      setNoProbateForState(false)
      const fieldsAnswered:ProbateField[] = await getFieldsAnswered() || []
      const probateDetailResponse = await probateApi.getProbateDetailsForLead(leadData["country_id"] , leadData["state_id"], fieldsAnswered)
      if(!probateDetailResponse) {
        console.log("no probate found for estate")
        setNoProbateForState(true)
        return
      }
      const unansweredField = probateDetailResponse?.getNextField(skipFields)
      if (!unansweredField) {
        navigate(SCREEN_PATHS.leadProbateReview)
      } else {
        setCurrentField(unansweredField)
      }
    } catch(e:any) {
        setError((e as any).message)
    }
  }

  const onAnswer = async (answer: string | number | boolean) => {
    try {
      setError(undefined);
      if(!lead?.key) {
        console.error("no lead available to set answer for")
        return
      }
      if(!saveFieldAnswered) {
        console.error("saveFieldAnswered not defined")
        return
      }

      if (currentField) {
        currentField.answerValue = answer;
        // save to db
        // await probateApi.saveFieldToKnowledgeEngineForLead(lead.key, currentField);
        // save to local field cache - injected as needed later(ProbateDetails.injectPreviouslyAnswered)
        await saveFieldAnswered(currentField);
        setScreenNumber(screenNumber + 1);
      } else {
        console.error("currentField not determined");
        setError("Sorry, could not determine the field.  Please try again");
      }
    } catch (error) {
      console.error(error);
      setError("Sorry, saving your answer failed.  Please try again");
    }
  };

  let child:JSX.Element = <Loading />
  if(currentField) {
    child = <Box
        sx={{
          my: 4,
        }}
      >
        <ProbateFieldQuestion field={currentField} onAnswer={onAnswer} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {error && (
            <Typography color="error" sx={{ my: 2 }}>
              {error}
            </Typography>
          )}
          <Button
            onClick={() => {
              currentField?.id && setSkipFields([...skipFields, currentField.id]);
              setScreenNumber(screenNumber + 1);
            }}
          >
            Skip
          </Button>
        </Box>
      </Box>
  }
  if(leadData && !leadData["country_id"] && !leadData["state_id"]) {
    child = <div style={{paddingTop:"2rem"}}>
      <CountryStateSelection />
    </div>
  }
  if(noProbateForState) {
    child = <div style={{paddingTop:"2rem"}}>
      <CountryStateSelection initialCountryId={leadData ? leadData["country_id"] : undefined} initialStateId={leadData ? leadData["state_id"] : undefined}/>
      <Typography variant="body1" component="p" color="error" style={{marginTop:"1rem"}}>
        We're sorry, we can't find any probate information for that state right now.
        Please email support@weareatticus.biz to see if we can help!
      </Typography>
    </div>
  }


  return (
    <ScreenContentWrapper
      pageSuffix="Probate"
      title="Get your Probate packet"
      step={1}
      totalSteps={10}
      showBeta={true}
      style={{padding:"1rem 3rem 1rem 3rem"}}
    >
      {child}
    </ScreenContentWrapper>
  );
};
